import React, {FC, useCallback, useEffect, useMemo} from "react";
import {
    Box, Button,
    Checkbox,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Typography,
} from "@mui/material";
import {useNavigate} from "react-router";
import CordwoodListPagination from "./CordwoodListPagination";
import TimberClassName from "../../timberClass/TimberClassDetail/TimberClassName";
import TimberTypeName from "../../timberType/TimberTypeDetail/TimberTypeName";
import {
    useAssignShipmentCordwoodMutation, useGetCordwoodAdvancedPresentationQuery,
    useGetCordwoodPresentationQuery,
    useUpdateCordwoodMutation, useUpdateMultipleCordwoodMutation, useGetTotalQuery
} from "../../../apis/Cordwood.api.ts";
import {AsyncFormSelect, usePagination} from '../../../../share';
import {Shipment} from "../../../types/models/Shipment.ts";
import ShipmentName from "../ShipmentDetail/ShipmentName.tsx";
import CordwoodDetail from "./CordwoodDetail.tsx";
import {
    AssignShipmentCordwood,
    Cordwood,
    PrimaryCordwood,
    toString as toStringCordwood,
    UpdateEntityCordwood
} from "../../../../timber/types/models/Cordwood";
import {FormProvider, useForm} from "react-hook-form";
import CordwoodListPdfDialog from "./CordwoodListPdfDialog.tsx";
import {cuttingStateEditCheck, shippingStateCheck, shippingStateEditCheck} from "../../../stateChecks.ts";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import CordwoodName, {CordwoodNameDisplay} from "../../cordwood/CordwoodDetail/CordwoodName.tsx";
import useSearchDebounce from "../../../../hooks/useSearchDebounce.ts";
import {styled} from "@mui/material/styles";
import { Refresh } from "@mui/icons-material";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import * as crudgen from "../../../../share";
import {useStateSession} from "../../../../hooks/useAuth.ts";
import {useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import {PrimaryCutting} from "../../../types/models/Cutting.ts";

import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";
import {CordwoodMeasurementType} from "../../../types/models/CordwoodMeasurementType";

const SmallTableHeaderRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
}));

const SmallTableRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        cursor: "pointer",
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
}));

const BigTableHeaderRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
}));

const BigTableRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        cursor: "pointer",
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
}));

type IFormSelectProps = {
    shipment: Shipment;
}

const CordwoodListItemForShipment: FC<IFormSelectProps> = ({shipment}) => {

    const session = useStateSession()
    const { data: shipmentCutting } = useGetCuttingQuery(PrimaryCutting.fromId(shipment?.cuttingId), {skip: !shipment?.cuttingId})
    const allowsMultiStorage = shipmentCutting?.allowsMultiStorageShipments === true
    const navigate = useNavigate()
    const { page, pageSize, handleChangePage, handleChangePageSize } = usePagination('ShipmentCordwood')
    const [cordwood, setCordwood] = React.useState<Cordwood | undefined>(undefined);
    const [allCheckbox, setAllCheckbox] = React.useState<boolean>(true);

    const methods = useForm({ defaultValues: { cordwoodId: null } });
    
    const {
        data: list = {
            items: [],
            totalItems: 0,
            page: 0,
            pageSize: 10,
            numPages: 0,
        },
        isLoading: getCordwoodListLoading,
        isFetching: getCordwoodListFetching,
        refetch: getCordwoodListRefetch,
    } = useGetCordwoodPresentationQuery({
        list: {
            page,
            pageSize,
        },
        filter: {
            shipmentIdRefs: [shipment.id],
        } 
    })

    const {
        data: cordwoodTotals = [],
        isLoading: getCordwoodTotalLoading,
        refetch: getCordwoodTotalRefetch,
    } = useGetTotalQuery({
        shipmentId: shipment?.id,
    }, {skip: !shipment?.id})

    const INIT = {
        totalCommitedCount: 0,
        totalCount: 0,
        totalCommitedVolume: 0,
        totalVolume: 0,
    };

    const getTotals = (totals) => {
        if (totals?.length === 1) {
            return {
                totalCommitedCount: totals[0].commited ? totals[0].totalCount : 0,
                totalCount: totals[0].totalCount,
                totalCommitedVolume: totals[0].commited ? totals[0].totalVolume : 0,
                totalVolume: totals[0].totalVolume,
            };
        } else if (totals?.length === 2) {
            const commitedIndex = totals[0].commited ? 0 : 1;
            const notCommitedIndex = 1 - commitedIndex;
            return {
                totalCommitedCount: totals[commitedIndex].totalCount,
                totalCount: totals[0].totalCount + totals[1].totalCount,
                totalCommitedVolume: totals[commitedIndex].totalVolume,
                totalVolume: totals[0].totalVolume + totals[1].totalVolume,
            };
        } else {
            return INIT;
        }
    };

    const [totalsState, setTotalsState] = React.useState(INIT)

    React.useEffect(() => {
        getCordwoodTotalRefetch()
        const state = getTotals(cordwoodTotals)
        setTotalsState(state)
    }, [list, cordwoodTotals, getCordwoodTotalRefetch])
    
    const shipmentEditable = shippingStateEditCheck(shipment?.state)

    const [, setCordwoodInputValue, cordwoodInputTerm] = useSearchDebounce();
    const { data: cordwoodSearchList = {
        items: [],
        totalItems: 0,
        page: 0,
        pageSize: 12,
        numPages: 0
    }, 
        isLoading: cordwoodSearchListLoading,
        isFetching: cordwoodSearchListFetching,
    } = useGetCordwoodAdvancedPresentationQuery({
        list: {
            page: 0,
            pageSize: 12,
        },
        filter: {
            internalKey: cordwoodInputTerm?.toString(),
            shipmentIdInclude: true,
            cuttingIdRefs: [shipment?.cuttingId],
            ...(allowsMultiStorage ? {} : { storageIdRefs: [shipment?.storageId] }),
        },
    }, {
        skip: !(shipment && cordwoodInputTerm && cordwoodInputTerm.length > 0),
    })

    const [assignShipmentCordwoodMutation, { isLoading: assignCordwoodLoading }] = useAssignShipmentCordwoodMutation()
    const [updateCordwoodMutation, { isLoading: updateCordwoodLoading }] = useUpdateCordwoodMutation()
    const [updateMultipleCordwoodMutation, { isLoading: updateMultipleCordwoodLoading }] = useUpdateMultipleCordwoodMutation()

    const assignCordwood = useCallback(async (assign: AssignShipmentCordwood) => {
        const data = await assignShipmentCordwoodMutation(assign).unwrap();
        setCordwood(data)
    }, [assignShipmentCordwoodMutation, navigate])

    const updateCordwood = useCallback(async (cordwood: UpdateEntityCordwood) => {
        const data = await updateCordwoodMutation(cordwood).unwrap();
        setCordwood(data)
    }, [updateCordwoodMutation, navigate])
    
    // todo check if shipment can be changed
    const cordwoodAssignHandler = useCallback((cordwood: Cordwood, volume: number, measurementType: CordwoodMeasurementType) => {
        if (shipment) {
            // todo check if shipment was already set
            const assign: AssignShipmentCordwood = {
                id: cordwood.id,
                shipmentId: shipment.id,
                volume,
                measurementType,
            }
            assignCordwood(assign)
            // methods.setValue('timberId', null)
        }
    }, [shipment, assignCordwood, setCordwood])

    const cordwoodRemove = useCallback((cordwood: Cordwood) => {
        if (shipment) {
            let update: UpdateEntityCordwood = {
                id: PrimaryCordwood.fromEntity(cordwood),
                data: {
                    ...cordwood,
                    shipmentId: undefined,
                }
            }
            updateCordwood(update)
            setCordwoodInputValue('')
            // methods.setValue('timberId', null)
        }
    }, [shipment, updateCordwood, setCordwood])

    const { data: cutting } = useGetCuttingQuery(PrimaryCutting.fromId(shipment?.cuttingId), {skip: !shipment?.cuttingId})

    const handleCommited = useCallback(async (cordwood: Cordwood) => {
        if (shipment) {
            const update: UpdateEntityCordwood = {
                id: PrimaryCordwood.fromEntity(cordwood),
                data: {
                    ...cordwood,
                    commited: !cordwood.commited,
                }
            }
            await updateCordwoodMutation(update)
        }
    }, [shipment, updateCordwood])

    const handleAllCommited = useCallback(async (commited) => {
        if (shipment && list) {
            const updates: UpdateEntityCordwood[] = list.items.map(cordwood => {
                return {
                    ...cordwood,
                    commited,
                }
            })
            await updateMultipleCordwoodMutation(updates)
            getCordwoodListRefetch()
        }
    }, [list, shipment])

    useEffect(() => {
        let countCommited = 0
        for (const cordwood of list.items) {
            if (cordwood.commited) {
                countCommited++
            }
        }

        if (countCommited > list.items.length / 2) {
            setAllCheckbox(true)
        } else {
            setAllCheckbox(false)
        }
    }, [list, setAllCheckbox])

    const showEdit = session.hasPermission('', 'applications/timber/entities/Shipments/update/view')
        && cuttingStateEditCheck(cutting?.state)
        && shippingStateEditCheck(shipment?.state)
        && (!shipment || shippingStateEditCheck(shipment?.state))
        && shippingStateCheck(session, shipment?.state)

    return useMemo(() => (

    <Box data-testid="Shipment-cordwoods-container">
        <Paper>
            <Toolbar
                sx={ {
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                } }
                data-testid="Shipment-cordwoods-toolbar"
            >
                <Typography
                    sx={ { flex: '1 1 100%' } }
                    variant="h6"
                    component="div"
                >
                    <Trans>Shipment Cordwood</Trans>
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <CordwoodListPdfDialog shipment={shipment} copies={4}/>
                    <Button onClick={getCordwoodListRefetch}>
                        <Refresh />
                    </Button>
                </Box>
            </Toolbar>
            {shipment && !getCordwoodListLoading && totalsState &&
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Table size="small" sx={{ width: 'auto', minWidth: 300 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell align="right"><b><Trans>Commited / Total</Trans></b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell><b>{shipment.state} <Trans>Count</Trans></b></TableCell>
                                <TableCell align="right">{totalsState.totalCommitedCount} / {totalsState.totalCount}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell><b>{shipment.state} <Trans>Volume (m&#xB3;)</Trans></b></TableCell>
                                <TableCell align="right">{fixedFormat(totalsState.totalCommitedVolume)} / {fixedFormat(totalsState.totalVolume)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            }
            <FormProvider {...methods}>
                <AsyncFormSelect
                    name='cordwoodId'
                    label={t`Select cordwood to shipment`}
                    options={ cordwoodSearchList.items }
                    optionsLoading={ cordwoodSearchListLoading || cordwoodSearchListFetching }
                    optionLabelMap={ toStringCordwood }
                    onInputChange={setCordwoodInputValue}
                    onValueChange={setCordwood}
                    renderOption={(optionProps, option, { inputValue }) => {
                        const { key, ...filteredOptionProps } = optionProps;
                        return (<li key={key} {...filteredOptionProps}><CordwoodNameDisplay data={option}/></li>)
                    } }
                    renderValue={(value, getItemProps) =>
                        (Array.isArray(value) ? value : [value]).map((option: any, index: number) => {
                            const { key: _key, ...tagProps } = getItemProps({ index })
                            return (
                                <crudgen.SelectChip key={index} {...tagProps}>
                                    <CordwoodName id={option}/>
                                </crudgen.SelectChip>
                            );
                        })
                    }
                    data-testid="Shipment-cordwood-select"
                />
    
                {cordwood && (
                    <CordwoodDetail
                        shipment={shipment}
                        data={cordwood!}
                        onAddCordwood={shipmentEditable && cordwoodAssignHandler}
                        onRemoveCordwood={shipmentEditable && cordwoodRemove}
                        data-testid="Shipment-cordwood-detail"
                    />
                )}

                { getCordwoodListLoading ?
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <GradientCircularProgress/>
                    </Box>
                    :
                    <Paper>
                        { getCordwoodListFetching && <GradientCircularProgress absolute={true}/> }
                        <TableContainer data-testid="Shipment-cordwoods-table-container">
                            <Table stickyHeader aria-label="simple table" size="small" data-testid="Shipment-cordwoods-table">
                                <TableHead>
                                    <SmallTableHeaderRow>
                                        {showEdit &&
                                            <TableCell component="th" align="center">
                                                <Trans>Commit</Trans>
                                                <Checkbox
                                                    color="primary"
                                                    checked={allCheckbox}
                                                    onClick={(e) => handleAllCommited(!allCheckbox)}
                                                />
                                            </TableCell>
                                        }
                                        <TableCell component="th" align="center"><Trans>No</Trans></TableCell>
                                        <TableCell component="th" align="left"><Trans>Timber Key</Trans></TableCell>
                                        <TableCell component="th" align="right"><Trans>Volume (m&#xB3;)</Trans></TableCell>
                                        <TableCell component="th" align="left"><Trans>Timber Type</Trans></TableCell>
                                        {allowsMultiStorage &&
                                            <TableCell component="th" align="left"><Trans>Shipment</Trans></TableCell>
                                        }
                                    </SmallTableHeaderRow>
                                    <BigTableHeaderRow>
                                        {showEdit &&
                                            <TableCell component="th" align="center">
                                                <Trans>Commit</Trans>
                                                <Checkbox
                                                    color="primary"
                                                    checked={allCheckbox}
                                                    onClick={(e) => handleAllCommited(!allCheckbox)}
                                                />
                                            </TableCell>
                                        }
                                        <TableCell component="th" align="center"><Trans>No</Trans></TableCell>
                                        <TableCell component="th" align="center"><Trans>Timber Key</Trans></TableCell>
                                        <TableCell component="th" align="center"><Trans>Volume</Trans></TableCell>
                                        <TableCell component="th" align="center"><Trans>Timber Type</Trans></TableCell>
                                        <TableCell component="th" align="center"><Trans>Class</Trans></TableCell>
                                        {allowsMultiStorage &&
                                            <TableCell component="th" align="center"><Trans>Shipment</Trans></TableCell>
                                        }
                                    </BigTableHeaderRow>
                                </TableHead>
                                <TableBody>
                                    { list.items.map((cordwood, index) => (
                                        <React.Fragment key={ PrimaryCordwood.fromEntity(cordwood).toPath() }>
                                            <SmallTableRow
                                                hover={true}
                                                key={ PrimaryCordwood.fromEntity(cordwood).toPath() + '-small' }
                                            >
                                                { showEdit &&
                                                    <TableCell scope="row" align="center">
                                                        <Checkbox
                                                            color="primary"
                                                            checked={cordwood.commited}
                                                            onClick={(e) => handleCommited(cordwood)}
                                                        />
                                                    </TableCell>
                                                }
                                                <TableCell scope="row" align="center"
                                                           onClick={() => setCordwood(cordwood)}
                                                >
                                                    { (page * pageSize) + index + 1 }.
                                                </TableCell>
                                                <TableCell scope="row"
                                                           onClick={() => setCordwood(cordwood)}
                                                >
                                                    <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                        { cordwood.timberKey ?
                                                            <>
                                                                { cordwood.timberKey }
                                                            </>
                                                            :
                                                            '-'
                                                        }
                                                    </Typography>
                                                    <Typography component="div" variant="body1">
                                                        { cordwood.internalKey &&
                                                            <>
                                                                <Trans>IK</Trans>:&nbsp;
                                                                { cordwood.internalKey }
                                                            </>
                                                        }
                                                    </Typography>
                                                </TableCell>
                                                <TableCell scope="row" align="right"
                                                           onClick={() => setCordwood(cordwood)}
                                                >
                                                    <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                        { cordwood.volume ?
                                                            <>
                                                                {fixedFormat(cordwood.volume) } {cordwood.measurementType}
                                                            </>
                                                            :
                                                            '-'
                                                        }
                                                    </Typography>

                                                </TableCell>
                                                <TableCell scope="row"
                                                           onClick={() => setCordwood(cordwood)}
                                                >
                                                    <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                        { cordwood.timberTypeId ?
                                                            <>
                                                                <TimberTypeName id={ cordwood.timberTypeId }/>
                                                            </>
                                                            :
                                                            '-'
                                                        }
                                                    </Typography>
                                                    <Typography component="div" variant="body1">
                                                        { cordwood.classId &&
                                                            <>
                                                                <Trans>C</Trans>:&nbsp;
                                                                <TimberClassName id={ cordwood.classId }/>
                                                            </>
                                                        }
                                                    </Typography>
                                                </TableCell>
                                                {allowsMultiStorage &&
                                                    <TableCell scope="row" align="left"
                                                               onClick={() => setCordwood(cordwood)}
                                                    >
                                                        { cordwood.shipmentId ?
                                                            <ShipmentName id={ cordwood.shipmentId }/>
                                                            :
                                                            '-'
                                                        }
                                                    </TableCell>
                                                }
                                            </SmallTableRow>
                                            <BigTableRow
                                                hover={true}
                                                key={ PrimaryCordwood.fromEntity(cordwood).toPath() }
                                            >
                                                {showEdit &&
                                                <TableCell scope="row" align="center">
                                                    <Checkbox
                                                        color="primary"
                                                        checked={cordwood.commited}
                                                        onClick={() => handleCommited(cordwood)}
                                                    />
                                                </TableCell> }
                                                <TableCell width="20%" scope="row" align="center"
                                                           onClick={(e) => setCordwood(cordwood)}
                                                >
                                                    { (page * pageSize) + index + 1 }.
                                                </TableCell>
                                                <TableCell width="20%" scope="row" align="center"
                                                           onClick={(e) => setCordwood(cordwood)}
                                                >
                                                    { cordwood.internalKey }
                                                </TableCell>
                                                <TableCell width="20%" scope="row" align="right"
                                                           onClick={(e) => setCordwood(cordwood)}
                                                >
                                                    {fixedFormat(cordwood.volume)} {cordwood.measurementType}
                                                </TableCell>
                                                <TableCell  width="20%" scope="row" align="center"
                                                            onClick={(e) => setCordwood(cordwood)}
                                                >
                                                    <TimberTypeName id={cordwood.timberTypeId}/>
                                                </TableCell>
                                                <TableCell width="20%" scope="row" align="center"
                                                           onClick={(e) => setCordwood(cordwood)}
                                                >
                                                    <TimberClassName id={cordwood.classId}/>
                                                </TableCell>
                                                {allowsMultiStorage &&
                                                    <TableCell width="20%" scope="row" align="center"
                                                               onClick={(e) => setCordwood(cordwood)}
                                                    >
                                                        { cordwood.shipmentId ?
                                                            <ShipmentName id={ cordwood.shipmentId }/>
                                                            :
                                                            '-'
                                                        }
                                                    </TableCell>
                                                }
                                            </BigTableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <CordwoodListPagination
                            totalItems={ list.totalItems }
                            page={Number(page)}
                            pageSize={pageSize}
                            handleChangePage={handleChangePage}
                            handleChangePageSize={handleChangePageSize}
                        />
                    </Paper>
                }
            </FormProvider>
        </Paper>
    </Box>
    ), [cordwoodSearchListLoading, cordwoodSearchListFetching, cordwoodSearchList, cordwood, setCordwood, cordwoodInputTerm, setCordwoodInputValue, 
        list, page, pageSize, handleChangePage, handleChangePageSize, cordwoodAssignHandler, handleAllCommited, handleCommited, showEdit, allCheckbox,
        getCordwoodListFetching, getCordwoodListLoading, totalsState, getCordwoodTotalLoading, allowsMultiStorage])
}

export default CordwoodListItemForShipment;
